import { useEffect, useRef, useState } from "react";
import api, { formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { Sparkles, Send, X, Loader2, MessageSquarePlus, RefreshCw } from "lucide-react";
import { Sheet, SheetContent, SheetTrigger } from "@/components/ui/sheet";

const SUGGESTIONS = [
  "Riassumi le criticità del giorno",
  "Qual è il RM con performance peggiore?",
  "Trend produttività HK ultimo mese",
  "Stato relazioni mensili",
];

export function AIChatTrigger() {
  const [open, setOpen] = useState(false);
  return (
    <Sheet open={open} onOpenChange={setOpen}>
      <SheetTrigger asChild>
        <button data-testid="ai-trigger" title="Assistente AI" className="h-10 w-10 rounded-xl border border-lavender-300/40 bg-lavender-100/30 dark:bg-lavender-900/20 hover:bg-lavender-100/60 dark:hover:bg-lavender-900/40 flex items-center justify-center transition-colors">
          <Sparkles className="h-[18px] w-[18px] text-lavender-600 dark:text-lavender-300" />
        </button>
      </SheetTrigger>
      <SheetContent side="right" className="w-full sm:w-[440px] p-0 flex flex-col" data-testid="ai-drawer">
        <AIChatBody onClose={() => setOpen(false)} />
      </SheetContent>
    </Sheet>
  );
}

function AIChatBody() {
  const [sessionId, setSessionId] = useState(() => localStorage.getItem("ih_ai_session"));
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState("");
  const [sending, setSending] = useState(false);
  const bottomRef = useRef(null);

  useEffect(() => {
    if (sessionId) {
      api.get(`/ai/chat/sessions/${sessionId}`).then((r) => setMessages(r.data.messages || [])).catch(() => { localStorage.removeItem("ih_ai_session"); setSessionId(null); });
    }
  }, [sessionId]);

  useEffect(() => { bottomRef.current?.scrollIntoView({ behavior: "smooth" }); }, [messages, sending]);

  const send = async (text) => {
    const msg = (text || input).trim();
    if (!msg || sending) return;
    setInput("");
    setMessages((m) => [...m, { role: "user", content: msg, timestamp: new Date().toISOString() }]);
    setSending(true);
    try {
      const r = await api.post("/ai/chat", { session_id: sessionId, message: msg });
      if (!sessionId) {
        setSessionId(r.data.session_id);
        localStorage.setItem("ih_ai_session", r.data.session_id);
      }
      setMessages((m) => [...m, r.data.message]);
    } catch (err) {
      toast.error(formatApiError(err));
      setMessages((m) => [...m, { role: "assistant", content: "⚠️ Errore: " + formatApiError(err), timestamp: new Date().toISOString(), error: true }]);
    } finally { setSending(false); }
  };

  const newConversation = () => {
    localStorage.removeItem("ih_ai_session");
    setSessionId(null);
    setMessages([]);
  };

  return (
    <>
      <div className="p-5 border-b border-border/60 flex items-center gap-3">
        <div className="h-10 w-10 rounded-xl bg-gradient-to-br from-lavender-300 to-lavender-500 flex items-center justify-center"><Sparkles className="h-5 w-5 text-white" /></div>
        <div className="flex-1">
          <h2 className="font-display text-lg tracking-tight">Assistente IH AuditFlow</h2>
          <div className="text-[10px] uppercase tracking-wider text-muted-foreground">Powered by gemini-3-flash</div>
        </div>
        <button onClick={newConversation} title="Nuova conversazione" className="h-8 w-8 rounded-lg hover:bg-accent flex items-center justify-center"><MessageSquarePlus className="h-4 w-4" /></button>
      </div>

      <div className="flex-1 overflow-y-auto p-4 space-y-3" data-testid="ai-messages">
        {messages.length === 0 && (
          <div className="space-y-3">
            <p className="text-sm text-muted-foreground">Ciao! Posso aiutarti con domande sui tuoi hotel, performance, ispezione, relazioni. Prova uno di questi:</p>
            <div className="flex flex-col gap-2">
              {SUGGESTIONS.map((s) => (
                <button key={s} onClick={() => send(s)} data-testid={`ai-sugg-${s.slice(0, 10)}`} className="text-left text-xs px-3 py-2 rounded-xl border border-border/60 hover:bg-accent/30 transition-colors">✨ {s}</button>
              ))}
            </div>
          </div>
        )}
        {messages.map((m, i) => (
          <div key={i} className={`flex gap-2 ${m.role === "user" ? "justify-end" : "justify-start"}`}>
            {m.role === "assistant" && <div className="h-7 w-7 rounded-lg bg-lavender-200 dark:bg-lavender-900/40 flex items-center justify-center shrink-0"><Sparkles className="h-3.5 w-3.5 text-lavender-600" /></div>}
            <div className={`max-w-[80%] rounded-2xl px-3 py-2 text-sm whitespace-pre-wrap ${m.role === "user" ? "bg-primary text-primary-foreground" : m.error ? "bg-rose-50 dark:bg-rose-900/20 border border-rose-300/40" : "bg-accent/40"}`}>
              {m.content}
            </div>
          </div>
        ))}
        {sending && (
          <div className="flex gap-2 justify-start">
            <div className="h-7 w-7 rounded-lg bg-lavender-200 dark:bg-lavender-900/40 flex items-center justify-center"><Sparkles className="h-3.5 w-3.5 text-lavender-600 animate-pulse" /></div>
            <div className="rounded-2xl bg-accent/40 px-3 py-2 text-sm flex gap-1">
              <span className="animate-bounce">·</span><span className="animate-bounce" style={{ animationDelay: "0.15s" }}>·</span><span className="animate-bounce" style={{ animationDelay: "0.3s" }}>·</span>
            </div>
          </div>
        )}
        <div ref={bottomRef} />
      </div>

      <form onSubmit={(e) => { e.preventDefault(); send(); }} className="p-3 border-t border-border/60 flex gap-2">
        <input data-testid="ai-input" type="text" value={input} onChange={(e) => setInput(e.target.value)} placeholder="Scrivi un messaggio…" disabled={sending} maxLength={2000} className="flex-1 rounded-xl border border-border bg-background px-3 py-2 text-sm focus:outline-none focus:border-lavender-300" />
        <button type="submit" disabled={sending || !input.trim()} data-testid="ai-send" className="h-10 w-10 rounded-xl bg-primary text-primary-foreground flex items-center justify-center disabled:opacity-50">
          {sending ? <Loader2 className="h-4 w-4 animate-spin" /> : <Send className="h-4 w-4" />}
        </button>
      </form>
    </>
  );
}

export function AIInsightCard({ endpoint, params = {}, title, autoLoad = false, sections = ["highlights", "to_monitor"] }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const load = async (refresh = false) => {
    setLoading(true); setError(null);
    try {
      const q = new URLSearchParams({ ...params, ...(refresh ? { refresh: "true" } : {}) }).toString();
      const r = await api.get(`${endpoint}?${q}`);
      setData(r.data);
    } catch (err) {
      setError(formatApiError(err));
    } finally { setLoading(false); }
  };

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { if (autoLoad) load(); }, []);

  return (
    <div className={`ih-glass rounded-2xl p-5 border-l-4 border-l-lavender-400 ${loading ? "animate-pulse-border" : ""}`} data-testid={`ai-insight-${endpoint.replace(/\//g, "-")}`}>
      <div className="flex items-center justify-between mb-3">
        <div className="flex items-center gap-2">
          <Sparkles className="h-4 w-4 text-lavender-500" />
          <h3 className="font-display text-base tracking-tight">{title}</h3>
          {data?.cached && <span className="px-1.5 py-0.5 rounded-full text-[9px] uppercase bg-lavender-100 text-lavender-700 dark:bg-lavender-900/40 dark:text-lavender-300" title={`Cached. Generato il ${new Date(data.generated_at).toLocaleString("it-IT")}`}>Cache</span>}
        </div>
        {data && <button onClick={() => load(true)} className="h-7 w-7 rounded-lg hover:bg-accent flex items-center justify-center" title="Rigenera"><RefreshCw className="h-3 w-3" /></button>}
      </div>
      {error && <div className="text-xs text-rose-600 dark:text-rose-400 mb-2">{error}</div>}
      {!data && !loading && (
        <button onClick={() => load()} data-testid="ai-generate" className="text-xs px-3 py-1.5 rounded-full bg-primary text-primary-foreground font-medium hover:opacity-90">✨ Genera insight</button>
      )}
      {loading && <div className="text-xs text-muted-foreground flex items-center gap-2"><Loader2 className="h-3 w-3 animate-spin" /> Generazione in corso…</div>}
      {data && (
        <div className="space-y-2">
          <p className="text-sm leading-relaxed whitespace-pre-wrap">{data.summary || data.briefing}</p>
          {sections.map((sec) => data[sec]?.length > 0 && (
            <div key={sec}>
              <div className="text-[10px] uppercase tracking-wider text-muted-foreground mt-2 mb-1">{sec.replace("_", " ")}</div>
              <ul className="text-xs space-y-0.5">
                {data[sec].map((it, i) => <li key={i} className="flex gap-1.5"><span className="text-lavender-500">•</span><span>{it}</span></li>)}
              </ul>
            </div>
          ))}
          <div className="text-[10px] text-muted-foreground/70 mt-2 pt-2 border-t border-border/30">Generato il {new Date(data.generated_at).toLocaleString("it-IT")} · {data.model}</div>
        </div>
      )}
    </div>
  );
}
